import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { PageHeader, Tag, StatCard } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileSignature, CheckCircle2, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { formatDate } from "@/lib/dateFormat";

const CONTRACT_TYPES = [
  { v: "SOW", label: "SOW · Statement of Work" },
  { v: "NDA", label: "NDA · Non-Disclosure" },
  { v: "MSA", label: "MSA · Master Services" },
];

/** "Active" = status is one of these (the DB has used both labels over time). */
const ACTIVE_CONTRACT_STATUSES = new Set(["approved", "signed", "active", "completed"]);
const isActiveContract = (r) => ACTIVE_CONTRACT_STATUSES.has(r?.status);
// Phase 45 — unsigned contracts are treated as overdue regardless of age.
const isUnsigned = (r) => r?.status === "unsigned";

function daysSince(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

function ageTone(d, isApproved) {
  if (isApproved) return "green";
  if (d >= 15) return "red";
  if (d >= 7) return "orange";
  return "slate";
}

function statusTone(s) {
  if (ACTIVE_CONTRACT_STATUSES.has(s)) return "green";
  if (s === "sent") return "blue";
  if (s === "unsigned") return "red";
  return "slate"; // draft
}

const emptyForm = () => ({
  contract_type: "SOW", title: "", project_id: "", client_user_id: "",
  deal_id: "",  // Phase 84.8 — CRM deal linkage
  link: "", notes: "", value: "",
});

export default function Contracts({ embedded = false }) {
  const { user } = useAuth();
  const can = usePermissions();
  const canCreateContract = can("contracts_create");
  const canEditContract = can("contracts_edit");
  const canManage = canCreateContract || canEditContract || can("contracts_delete");
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [deals, setDeals] = useState([]);         // Phase 84.8 — for deal picker
  const [dealSuggestion, setDealSuggestion] = useState(null);  // Phase 84.8
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState("ALL");
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/contracts");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    if (canManage) {
      api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
      // Phase 84.8 — load non-lost deals so the operator can pick / override the auto-suggestion.
      api.get("/crm/deals").then((r) => setDeals((r.data || []).filter((d) => d.stage !== "Lost"))).catch(() => {});
    }
  }, [canManage]);

  // Phase 84.8 — auto-open the create dialog when landing with ?new=1 (from
  // the global "+ Create → New contract" quick-action). Strip the param for
  // everyone (even non-managers) so the URL doesn't stay noisy on back-nav.
  useEffect(() => {
    if (params.get("new") !== "1") return;
    if (canManage) setOpen(true);
    params.delete("new");
    setParams(params, { replace: true });
  }, [params, setParams, canManage]);

  const suggestedForClientRef = useRef(null);
  // Phase 84.8 — whenever the operator picks a client in the create dialog,
  // hit the backend suggest endpoint and pre-fill the deal picker.
  //
  // Code-review fix (iter79) — track WHICH client we've auto-suggested for
  // in a ref so a manual "unlink" (form.deal_id → "") isn't overwritten by
  // the effect re-running.
  useEffect(() => {
    if (!open) { setDealSuggestion(null); suggestedForClientRef.current = null; return; }
    if (!form.client_user_id) { setDealSuggestion(null); return; }
    let cancelled = false;
    api.get(`/contracts/suggest-deal/${form.client_user_id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setDealSuggestion(data);
        // Auto-fill ONCE per (client, dialog-open) cycle — after that,
        // whatever the operator picks (including "No deal") wins.
        const already = suggestedForClientRef.current === form.client_user_id;
        if (!already && data?.suggested_deal_id) {
          setForm((f) => ({ ...f, deal_id: f.deal_id || data.suggested_deal_id }));
        }
        suggestedForClientRef.current = form.client_user_id;
      })
      .catch(() => setDealSuggestion(null));
    return () => { cancelled = true; };
  }, [form.client_user_id, open]);

  const create = async () => {
    try {
      await api.post("/contracts", { ...form, value: Number(form.value) || 0, deal_id: form.deal_id || null });
      toast.success(form.deal_id ? "Contract created & linked to deal" : "Contract created");
      setOpen(false);
      setForm(emptyForm());
      setDealSuggestion(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/contracts/${id}`, { status }); load(); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [sigOpen, setSigOpen] = useState(false);
  const [sigTarget, setSigTarget] = useState(null);
  const openSign = (row) => { setSigTarget(row); setSigOpen(true); };
  const handleSign = async (payload) => {
    if (!sigTarget) return;
    try {
      await api.post(`/contracts/${sigTarget.id}/sign`, payload);
      toast.success("Signed — contract is now active");
      setSigOpen(false); setSigTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const projectsMap = Object.fromEntries(projects.map((p) => [p.id, p]));
  const clientsMap = Object.fromEntries(clients.map((c) => [c.id, c]));
  // Phase 84.8 — deal lookup for "Deal · X" chip on cards/rows.
  const dealsMap = Object.fromEntries((deals || []).map((d) => [d.id, d]));

  const filtered = useMemo(() => {
    if (filter === "ALL") return rows;
    if (filter === "OVERDUE") return rows.filter((r) => !isActiveContract(r) && (isUnsigned(r) || daysSince(r.created_at) >= 7));
    return rows.filter((r) => r.contract_type === filter);
  }, [rows, filter]);

  // Phase 45 — Edit dialog state
  const [editing, setEditing] = useState(null);
  const startEdit = (r) => setEditing({ ...r });
  const saveEdit = async () => {
    if (!editing) return;
    try {
      const payload = { title: editing.title, contract_type: editing.contract_type, link: editing.link, notes: editing.notes, value: Number(editing.value || 0), project_id: editing.project_id || null, client_user_id: editing.client_user_id || null };
      await api.patch(`/contracts/${editing.id}`, payload);
      toast.success("Contract updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const summary = useMemo(() => {
    const open = rows.filter((r) => !isActiveContract(r));
    // Phase 45 — any "unsigned" tag counts as overdue regardless of age.
    const overdueRows = open.filter((r) => isUnsigned(r) || daysSince(r.created_at) >= 15);
    return {
      total: rows.length,
      open: open.length,
      approved: rows.filter(isActiveContract).length,
      overdue: overdueRows.length,
      orange: open.filter((r) => !isUnsigned(r) && (() => { const d = daysSince(r.created_at); return d >= 7 && d < 15; })()).length,
    };
  }, [rows]);

  return (
    <div className={embedded ? "fade-in" : "p-4 sm:p-8 max-w-7xl mx-auto fade-in"} data-testid="contracts-page">
      {embedded ? (
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4" data-testid="contracts-embedded-actions">
          <div className="text-[11px] uppercase tracking-wider text-slate-400">
            Every SOW · NDA · MSA. Draft → sent → signed. Pending &gt; 7 days turn orange, &gt; 15 days red.
          </div>
          {canCreateContract && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-contract-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
                  <Plus size={14} className="mr-1.5" /> New contract
                </Button>
              </DialogTrigger>
              <NewContractDialogBody form={form} setForm={setForm} projects={projects} clients={clients} deals={deals} dealSuggestion={dealSuggestion} create={create} />
            </Dialog>
          )}
        </div>
      ) : (
        <PageHeader
          eyebrow="Legal"
          title="Contracts · SOW · NDA · MSA"
          subtitle="Track every document from draft → sent → signed. Pending > 7 days turn orange, > 15 days turn red."
          actions={canCreateContract && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-contract-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
                  <Plus size={14} className="mr-1.5" /> New contract
                </Button>
              </DialogTrigger>
              <NewContractDialogBody form={form} setForm={setForm} projects={projects} clients={clients} deals={deals} dealSuggestion={dealSuggestion} create={create} />
            </Dialog>
          )}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total" value={summary.total} />
        <StatCard label="Pending" value={summary.open} accent="text-slate-600" />
        <StatCard label=">7 days" value={summary.orange} accent="text-orange-700" />
        <StatCard label=">15 days" value={summary.overdue} accent="text-red-700" />
        <StatCard label="Signed" value={summary.approved} accent="text-green-700" />
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {["ALL", "SOW", "NDA", "MSA", "OVERDUE"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`contract-filter-${f}`}
            className={`text-xs px-3 py-1.5 rounded-sm border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
            {f}
          </button>
        ))}
      </div>

      <ContractsListing
        filtered={filtered}
        projectsMap={projectsMap}
        clientsMap={clientsMap}
        dealsMap={dealsMap}
        canManage={canEditContract}
        userRole={user?.role}
        updateStatus={updateStatus}
        openSign={openSign}
        onEdit={startEdit}
      />
      <SignaturePad open={sigOpen} onClose={() => setSigOpen(false)} onSign={handleSign} signerNameDefault={user?.name} />

      {/* Phase 45 — Edit contract dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="contract-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit contract</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <div>
                <Label className="label-overline">Type</Label>
                <Select value={editing.contract_type} onValueChange={(v) => setEditing({ ...editing, contract_type: v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTRACT_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-overline">Title</Label><Input className="rounded-sm mt-1" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="contract-edit-title" /></div>
              <div><Label className="label-overline">Drive / file link</Label><Input className="rounded-sm mt-1" value={editing.link || ""} onChange={(e) => setEditing({ ...editing, link: e.target.value })} data-testid="contract-edit-link" placeholder="https://drive.google.com/…" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-overline">Value ₹</Label><Input type="number" className="rounded-sm mt-1" value={editing.value || ""} onChange={(e) => setEditing({ ...editing, value: e.target.value })} /></div>
                <div>
                  <Label className="label-overline">Client</Label>
                  <Select value={editing.client_user_id || "none"} onValueChange={(v) => setEditing({ ...editing, client_user_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— None —</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="label-overline">Project</Label>
                <Select value={editing.project_id || "none"} onValueChange={(v) => setEditing({ ...editing, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-overline">Notes</Label><Textarea rows={2} className="rounded-sm mt-1" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="contract-edit-save">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Active (signed/approved) contracts → cards.
 * Everyone else (draft/sent) → list below, 10 most recent visible by default,
 * with a chevron toggle to reveal the rest. Sort: most recently updated first.
 * --------------------------------------------------------------------------*/
function ContractsListing({ filtered, projectsMap, clientsMap, dealsMap, canManage, userRole, updateStatus, openSign, onEdit }) {
  const [showAll, setShowAll] = useState(false);

  const byRecency = (a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
  const active = filtered.filter(isActiveContract).sort(byRecency);
  const other = filtered.filter((r) => !isActiveContract(r)).sort(byRecency);
  const visibleOther = showAll ? other : other.slice(0, 10);
  const hidden = other.length - visibleOther.length;

  if (filtered.length === 0) {
    return (
      <div className="card-flat p-12 text-sm text-slate-500 text-center inline-flex items-center justify-center gap-2 w-full">
        <FileSignature size={16} /> No contracts in this view.
      </div>
    );
  }

  return (
    <>
      {/* Active contracts — card grid */}
      {active.length > 0 && (
        <div data-testid="contracts-active-section">
          <div className="label-overline mb-2 text-slate-500">Active · {active.length}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="contracts-active-grid">
            {active.map((r) => (
              <ContractCard key={r.id} r={r} projectsMap={projectsMap} clientsMap={clientsMap} dealsMap={dealsMap} />
            ))}
          </div>
        </div>
      )}

      {/* Historical/pending — collapsed list */}
      {other.length > 0 && (
        <div className={active.length > 0 ? "mt-8" : ""} data-testid="contracts-historical-section">
          <div className="label-overline mb-2 text-slate-500">Pending &amp; historical · {other.length}</div>
          <div className="card-flat overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[900px]">
                <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-200 label-overline">
                  <div>Type</div>
                  <div className="col-span-3">Title</div>
                  <div className="col-span-2">Project</div>
                  <div className="col-span-2">Client</div>
                  <div>Age</div>
                  <div>Status</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                <div className="divide-row">
                  {visibleOther.map((r) => {
                    const d = daysSince(r.created_at);
                    const project = projectsMap[r.project_id];
                    const client = clientsMap[r.client_user_id];
                    return (
                      <div key={r.id} className="grid grid-cols-12 gap-4 px-5 py-3 text-sm items-center hover-row" data-testid={`contract-row-${r.id}`}>
                        <div><Tag tone="brand">{r.contract_type}</Tag></div>
                        <div className="col-span-3">
                          <div className="font-medium text-slate-900 truncate">{r.title}</div>
                          {r.notes && <div className="text-xs text-slate-500 truncate">{r.notes}</div>}
                        </div>
                        <div className="col-span-2 text-slate-600 truncate">{project?.name || "—"}</div>
                        <div className="col-span-2 text-slate-600 truncate">
                          {client?.name || "—"}
                          {r.deal_id && (
                            <div className="text-[10px] text-purple-700 truncate" data-testid={`contract-row-deal-${r.id}`}>
                              Deal · {(dealsMap || {})[r.deal_id]?.company_name || "linked"}
                            </div>
                          )}
                        </div>
                        <div><Tag tone={ageTone(d, false)}>{d}d</Tag></div>
                        <div><Tag tone={statusTone(r.status)}>{r.status}</Tag></div>
                        <div className="col-span-2 text-right flex items-center justify-end gap-1 flex-wrap">
                          {r.link && (
                            <a href={r.link} target="_blank" rel="noreferrer" className="p-1 rounded-sm border border-slate-200 hover:bg-slate-50" title="Open document"><ExternalLink size={12} /></a>
                          )}
                          {canManage && (
                            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs px-2" onClick={() => onEdit(r)} data-testid={`edit-contract-${r.id}`}>Edit</Button>
                          )}
                          {canManage && r.status === "draft" && (
                            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => updateStatus(r.id, "sent")} data-testid={`mark-sent-${r.id}`}>Mark sent</Button>
                          )}
                          {canManage && !isActiveContract(r) && r.status !== "unsigned" && (
                            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-red-200 text-red-700 hover:bg-red-50" onClick={() => updateStatus(r.id, "unsigned")} data-testid={`mark-unsigned-${r.id}`}>Unsigned</Button>
                          )}
                          {canManage && !isActiveContract(r) && (
                            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-green-200 text-green-700 hover:bg-green-50" onClick={() => updateStatus(r.id, "completed")} data-testid={`mark-completed-${r.id}`}>Mark completed</Button>
                          )}
                          {(canManage || userRole === "client") && !isActiveContract(r) && (
                            <Button size="sm" className="rounded-sm h-7 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => openSign(r)} data-testid={`mark-approved-${r.id}`}>
                              <CheckCircle2 size={12} className="mr-1" />Sign
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {hidden > 0 && (
                    <button
                      onClick={() => setShowAll(true)}
                      className="w-full px-4 py-3 text-xs text-[#F54800] hover:bg-orange-50 inline-flex items-center justify-center gap-1.5 font-medium"
                      data-testid="contracts-show-more"
                    >
                      <ChevronDown size={13} /> Show {hidden} more
                    </button>
                  )}
                  {showAll && other.length > 10 && (
                    <button
                      onClick={() => setShowAll(false)}
                      className="w-full px-4 py-3 text-xs text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5 font-medium"
                      data-testid="contracts-show-less"
                    >
                      <ChevronUp size={13} /> Show less
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ContractCard({ r, projectsMap, clientsMap, dealsMap }) {
  const project = projectsMap[r.project_id];
  const client = clientsMap[r.client_user_id];
  const deal = r.deal_id ? (dealsMap || {})[r.deal_id] : null;
  return (
    <div className="card-flat p-4" data-testid={`contract-${r.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Tag tone="brand">{r.contract_type}</Tag>
            <Tag tone="green">signed</Tag>
            {(deal || r.deal_id) && (
              <Tag tone="purple" data-testid={`contract-deal-chip-${r.id}`}>Deal · {deal?.company_name || "linked"}</Tag>
            )}
          </div>
          <div className="font-display font-bold text-sm text-slate-900 truncate">{r.title}</div>
          {client?.name && <div className="text-[11px] text-slate-500 truncate">{client.name}{client.company ? ` · ${client.company}` : ""}</div>}
        </div>
        {r.link && (
          <a href={r.link} target="_blank" rel="noreferrer noopener" className="shrink-0 text-slate-400 hover:text-slate-700 p-1.5" title="Open document"><ExternalLink size={11} /></a>
        )}
      </div>
      <div className="text-[11px] text-slate-600 space-y-0.5 mt-1">
        {project?.name && <div>Project · {project.name}</div>}
        {deal?.stage && <div>Stage · {deal.stage}</div>}
        {r.value > 0 && <div>Value · ₹{Number(r.value).toLocaleString("en-IN")}</div>}
        {r.signature?.signed_at && <div>Signed · {formatDate(r.signature.signed_at)}{r.signature.signer_name ? ` · ${r.signature.signer_name}` : ""}</div>}
      </div>
      {r.signature?.signature_url ? (
        <img src={r.signature.signature_url} alt="signature" className="h-7 border border-slate-200 rounded-sm bg-white mt-3" title={`Signed by ${r.signature.signer_name}`} />
      ) : r.signature?.typed_text ? (
        <span className="text-[13px] italic block mt-3" style={{ fontFamily: "'Brush Script MT', cursive" }}>{r.signature.typed_text}</span>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Extracted "New contract" dialog body — reused by both the standalone page
 * header actions slot and the embedded CRM > Contracts tab header actions.
 * ------------------------------------------------------------------------- */
function NewContractDialogBody({ form, setForm, projects, clients, deals, dealSuggestion, create }) {
  const linkedDealFromList = (deals || []).find((d) => d.id === form.deal_id);
  return (
    <DialogContent className="rounded-sm max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-display">New contract</DialogTitle>
        <DialogDescription className="text-[11px] text-slate-500">
          Create a SOW · NDA · MSA. Picking a client auto-links the most recent CRM deal so the signed document lands on the deal timeline + client card.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <div>
          <Label className="label-overline">Type</Label>
          <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="contract-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTRACT_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-overline">Title</Label>
          <Input className="rounded-sm mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Northstar SOW v1" data-testid="contract-title" />
        </div>
        <div>
          <Label className="label-overline">Project (optional)</Label>
          <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
            <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-overline">Client</Label>
          <Select value={form.client_user_id || "none"} onValueChange={(v) => setForm({ ...form, client_user_id: v === "none" ? "" : v, deal_id: v === "none" ? "" : form.deal_id })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="contract-client-select"><SelectValue placeholder="Pick client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Internal / no client</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `· ${c.company}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* Phase 84.8 — CRM deal linkage.
            Only rendered once a client is picked (the field is meaningless without one). */}
        {form.client_user_id && (
          <div data-testid="contract-deal-row">
            <Label className="label-overline">Linked CRM deal (optional)</Label>
            <Select value={form.deal_id || "none"} onValueChange={(v) => setForm({ ...form, deal_id: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="contract-deal-select">
                <SelectValue placeholder={dealSuggestion?.candidates?.length ? "Auto-suggested…" : "No deals for this client"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— (no deal)</SelectItem>
                {(dealSuggestion?.candidates || [])
                  .filter((d) => !(deals || []).some((x) => x.id === d.id))
                  .map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.company_name} · {d.stage}</SelectItem>
                  ))}
                {(deals || []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.company_name} · {d.stage}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {dealSuggestion?.suggested_deal_id && form.deal_id === dealSuggestion.suggested_deal_id && (
              <div className="mt-1 text-[10px] text-emerald-700 flex items-center gap-1" data-testid="contract-deal-suggested-note">
                Auto-linked from {linkedDealFromList?.company_name || "client"}&rsquo;s open deal.
              </div>
            )}
            {form.client_user_id && !dealSuggestion?.candidates?.length && (
              <div className="mt-1 text-[10px] text-slate-500" data-testid="contract-deal-empty-note">
                No open CRM deal found for this client — you can still link a deal manually.
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-overline">Document link</Label>
            <Input className="rounded-sm mt-1" placeholder="Google Drive / DocuSign URL" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
          </div>
          <div>
            <Label className="label-overline">Value (₹)</Label>
            <Input type="number" className="rounded-sm mt-1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          </div>
        </div>
        <div>
          <Label className="label-overline">Notes</Label>
          <Textarea rows={2} className="rounded-sm mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={create} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="create-contract">Create</Button>
      </DialogFooter>
    </DialogContent>
  );
}

